import React, { useEffect, useMemo, useState } from "react";

import { Button, Confirm, Icon, Modal } from "semantic-ui-react";
import { useIntl } from "react-intl";

import {
  ACTIONS,
  BTN_ACTS,
  EDUCATION_LEVEL_OPTIONS,
  OnEvent,
  PickedState,
  SEQUENCE_NAME,
  SetProp,
  EducationDataType,
  ExperienceDataType,
} from "./sequence/EmployeeInfo";

import ModEmployeeExperience from "./ModEmployeeExperience";
import ModEmployeePendingAlert from "./ModEmployeePendingAlert";

type CardExperienceInformationProps = Pick<
  PickedState,
  "buttonLoadCheck" | "changePassword" | "errorMessage" | "successMessage"
> & {
  setProp: SetProp;
  employeeId?: number;
  experience?: ExperienceType[];
  birthDate?: string | null;
  deleteExperienceData?: (id: number) => void;
  saveExperienceData?: (
    experienceData: ExperienceDataType,
    callback?: (result: { success: boolean }) => void
  ) => void;
  languageUX: "en" | "th";
};

type ExperienceType = {
  id?: number;
  tempId?: string;
  start_year?: string;
  startYear?: string;
  end_year?: string;
  endYear?: string;
  company_name?: string;
  companyName?: string;
};

const CardExperienceInformation = (props: CardExperienceInformationProps) => {
  const intl = useIntl();

  const [showExperienceInfoModal, setShowExperienceInfoModal] = useState<boolean>(false);
  const [showPendingAlert, setShowPendingAlert] = useState(false);
  const [editingExperience, setEditingExperience] = useState<any>(null);
  const [pendingExperience, setPendingExperience] = useState<ExperienceDataType[]>([]);
  const [showDeleteExperienceConfirm, setShowDeleteExperienceConfirm] = useState<boolean>(false);
  const [isSavingExperience, setIsSavingExperience] = useState(false);
  const [hasShownPendingAlert, setHasShownPendingAlert] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [deleteExperienceId, setDeleteExperienceId] = useState<number | null>(null);

  useEffect(() => {
    const saveStatus = props.buttonLoadCheck?.[BTN_ACTS.EXPERIENCE_SAVE];

    if (!isSavingExperience || saveStatus !== "SUCCESS") {
      return;
    }

    setIsSavingExperience(false);
    setShowExperienceInfoModal(false);
    setEditingExperience(null);
  }, [isSavingExperience, props.buttonLoadCheck?.[BTN_ACTS.EXPERIENCE_SAVE]]);

  const experienceInfoList = useMemo(
    () => [
      ...(props.experience?.map((exp, index) => ({
        ...exp,
        displayIndex: index + 1,
        isPending: false,
      })) ?? []),
      ...pendingExperience.map((exp, index) => ({
        ...exp,
        displayIndex: (props.experience?.length ?? 0) + index + 1,
        isPending: true,
      })),
    ],
    [pendingExperience, props.experience]
  );

  const handleSaveExperience = (experienceData: ExperienceDataType) => {
    if (props.employeeId) {
      setIsSavingExperience(true); // เพิ่มบรรทัดนี้
      props.saveExperienceData?.(experienceData, (result) => {
        if (result.success) {
          setIsSavingExperience(false);
          setShowExperienceInfoModal(false);
          setEditingExperience(null);
        }
      });
      return;
    }

    if (experienceData.tempId) {
      setPendingExperience((prev) =>
        prev.map((p) => (p.tempId === experienceData.tempId ? { ...experienceData } : p))
      );
    } else {
      const updateExperience = { ...experienceData, tempId: Date.now() };

      setPendingExperience((prev) => [...prev, updateExperience]);
    }

    setShowExperienceInfoModal(false);
    setEditingExperience(null);
  };

  const handleShowExperienceInfo = () => {
    if (props.employeeId || hasShownPendingAlert) {
      setEditingExperience(null);
      setShowExperienceInfoModal(true);

      return;
    }

    setShowPendingAlert(true);
    setHasShownPendingAlert(true);
  };

  const handleEditExperienceInfo = (experience: any, index: number) => {
    setEditingExperience({
      ...experience,
      index,
    });
    setShowExperienceInfoModal(true);
  };

  return (
    <div id="CardExperienceInformation">
      <div style={{ display: "flex", alignItems: "center" }}>
        <div
          id="CardExperienceInformation"
          style={{
            color: "rgba(26, 115, 232, 1)",
            fontSize: "0.95rem",
            margin: "1rem 0 0.5rem",
            fontWeight: "bold",
            minWidth: "200px",
          }}
        >
          {props.languageUX === "en" ? "Work Experience Information" : "ข้อมูลประวัติการทำงาน"}
        </div>

        <Button
          color="blue"
          onClick={handleShowExperienceInfo}
          size="small"
          style={{ padding: "8px 48px" }}
        >
          {props.languageUX === "en" ? "Add" : "เพิ่ม"}
        </Button>
      </div>

      <div>
        {experienceInfoList.map((exp) => (
          <div
            key={exp.id ?? exp.tempId}
            style={{
              alignItems: "center",
              backgroundColor: "rgba(254, 245, 245, 1)",
              borderRadius: "8px",
              display: "flex",
              fontWeight: "bold",
              justifyContent: "space-between",
              marginBottom: "1rem",
              padding: "1rem",
            }}
          >
            <div>
              {exp.start_year || exp.startYear} - {exp.end_year || exp.endYear} :{" "}
              {exp.company_name || exp.companyName}
            </div>
            <div style={{ display: "flex", gap: "12px", marginRight: "32px" }}>
              <Icon
                className="edit"
                style={{ color: "gray", cursor: "pointer" }}
                onClick={() => {
                  handleEditExperienceInfo(exp, exp.displayIndex);
                }}
              />
              <Icon
                className="trash alternate outline"
                style={{ color: "gray", cursor: "pointer" }}
                onClick={() => {
                  if (exp.isPending) {
                    setPendingExperience((prev) => prev.filter((p) => p.tempId !== exp.tempId));
                  } else {
                    setDeleteExperienceId(exp.id ?? null);
                    setShowDeleteExperienceConfirm(true);
                  }
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <ModEmployeePendingAlert
        open={showPendingAlert}
        onCancel={() => setShowPendingAlert(false)}
        onConfirm={() => {
          setShowPendingAlert(false);
          setEditingExperience(null);
          setShowExperienceInfoModal(true);
        }}
      />

      <ModEmployeeExperience
        setProp={props.setProp}
        birthDate={props.birthDate}
        experienceLength={props.experience?.length ?? 0}
        initialData={editingExperience}
        open={showExperienceInfoModal}
        pendingExperienceLength={pendingExperience.length}
        buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.EXPERIENCE_SAVE]}
        languageUX={props.languageUX}
        onClose={() => {
          setShowExperienceInfoModal(false);
          setEditingExperience(null);
        }}
        onSave={handleSaveExperience}
      />

      <Confirm
        header="ยืนยันการลบข้อมูลประวัติการทำงาน"
        open={showDeleteExperienceConfirm}
        onCancel={() => {
          setShowDeleteExperienceConfirm(false);
          setDeleteExperienceId(null);
        }}
        onConfirm={() => {
          if (deleteExperienceId) {
            props.deleteExperienceData?.(deleteExperienceId);
          }

          setShowDeleteExperienceConfirm(false);
          setDeleteExperienceId(null);
        }}
        cancelButton={{
          style: {
            borderRadius: "8px",
            width: "80px",
          },
          content: "ยกเลิก",
        }}
        confirmButton={{
          style: {
            backgroundColor: "var(--primary-theme-color)",
            borderRadius: "8px",
            color: "white",
            width: "80px",
          },
          content: "ลบ",
        }}
        content={
          !!deleteExperienceId &&
          (() => {
            const experienceList = props.experience;

            if (!experienceList) {
              return null;
            }
            const exp = experienceList.find((e) => e.id === deleteExperienceId);

            if (!exp) {
              return null;
            }

            return `คุณต้องการลบข้อมูลประวัติการทำงาน ${exp.start_year} - ${exp.end_year} : ${exp.company_name} หรือไม่?`;
          })()
        }
      />

      {showErrorModal && (
        <Modal
          open={showErrorModal}
          onClose={() => {
            setShowErrorModal(false);
            props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
          }}
        >
          <Modal.Content style={{ textAlign: "center" }}>
            <div style={{ fontWeight: "bold" }}>
              {(() => {
                const err = props.errorMessage?.[SEQUENCE_NAME];

                if (!err) return "Unknown error";

                if (typeof err === "string") return <div>{err}</div>;

                return Object.entries(err).flatMap(([key, messages]) =>
                  (messages as string[]).map((message, i) => (
                    <div key={`${key}-${i}`}>
                      {key}: {message}
                    </div>
                  ))
                );
              })()}
            </div>
          </Modal.Content>
        </Modal>
      )}
    </div>
  );
};

export default React.memo(CardExperienceInformation);
